import {
  AppTask,
  ConnectionUnauthorizedMetadata,
  ForecastChangedProduct,
  GroupedTaskMetadata,
  ProductsAddedMetadata,
  ReplenishProduct,
  ShipmentIncomingMetadata,
  ShipmentOverdueMetadata,
  ShipmentProduct,
  TaskMetadata,
  TaskProductBase,
  TaskType,
} from '@domain/tasks/models/Task';
import {
  ForecastChangedProductDTO,
  ReplenishProductDTO,
  ShipmentProductDTO,
  TaskDTO,
  TaskMetadataDTO,
  TaskProductBaseDTO,
} from '../dto/TaskDTO';

type ProductDTO = ForecastChangedProductDTO & ReplenishProductDTO & ShipmentProductDTO;

const mapProductBase = (product?: TaskProductBaseDTO): TaskProductBase => ({
  productId: product?.productId ?? '',
  title: product?.title,
  image: product?.image,
});

const mapForecastProduct = (product?: ProductDTO): ForecastChangedProduct => ({
  ...mapProductBase(product),
  quantity: product?.quantity,
  averageWeeklySales: product?.averageWeeklySales,
  changePercent: product?.changePercent,
  orderShiftDays: product?.orderShiftDays,
  orderWithinDays: product?.orderWithinDays,
  weeksOfStockLeft: product?.weeksOfStockLeft,
  weeksForecasted: product?.weeksForecasted,
  previousWeekly: product?.previousWeekly,
  currentWeekly: product?.currentWeekly,
});

const mapReplenishProduct = (product?: ProductDTO): ReplenishProduct => ({
  ...mapProductBase(product),
  quantity: product?.quantity,
  weeksOfStockLeft: product?.weeksOfStockLeft,
  orderWithinDays: product?.orderWithinDays,
  weeksForecasted: product?.weeksForecasted,
  stockStatus: product?.stockStatus,
});

const mapShipmentProduct = (product?: ProductDTO): ShipmentProduct => ({
  ...mapProductBase(product),
  quantity: product?.quantity,
});

const mapProductForType = (type: TaskType, product?: ProductDTO): TaskProductBase => {
  switch (type) {
    case TaskType.FORECAST_CHANGED:
      return mapForecastProduct(product);
    case TaskType.REPLENISH:
      return mapReplenishProduct(product);
    default:
      return mapShipmentProduct(product);
  }
};

const mapMetadata = (
  type: TaskType,
  metadata?: TaskMetadataDTO,
): TaskMetadata | GroupedTaskMetadata => {
  if (Array.isArray(metadata?.products)) {
    const products = metadata.products.map((product) => mapProductForType(type, product));
    const grouped: GroupedTaskMetadata = {
      product: products[0],
      products,
      totalQty: metadata.totalQty,
      etaDate: metadata.etaDate,
    };
    return grouped;
  }

  const product = metadata?.product;

  switch (type) {
    case TaskType.CONNECTION_UNAUTHORIZED: {
      const mapped: ConnectionUnauthorizedMetadata = {
        connectionId: metadata?.connectionId ?? '',
        connectionName: metadata?.connectionName,
      };
      return mapped;
    }
    case TaskType.FORECAST_CHANGED:
      return { product: mapForecastProduct(product) };
    case TaskType.REPLENISH:
      return { product: mapReplenishProduct(product) };
    case TaskType.SHIPMENT_OVERDUE: {
      const mapped: ShipmentOverdueMetadata = {
        product: mapShipmentProduct(product),
      };
      return mapped;
    }
    case TaskType.SHIPMENT_INCOMING: {
      const mapped: ShipmentIncomingMetadata = {
        product: mapShipmentProduct(product),
        etaDate: metadata?.etaDate ?? '',
        totalQty: metadata?.totalQty ?? 0,
      };
      return mapped;
    }
    case TaskType.PRODUCTS_ADDED:
    default: {
      const mapped: ProductsAddedMetadata = {
        product: mapProductBase(product),
      };
      return mapped;
    }
  }
};

export class TaskMapper {
  static toModel(dto: TaskDTO): AppTask {
    return {
      id: dto.id,
      companyId: dto.companyId,
      type: dto.type,
      title: dto.title,
      subtitle: dto.subtitle,
      metadata: mapMetadata(dto.type, dto.metadata),
      sourceIds: dto.sourceIds?.length ? dto.sourceIds : [dto.id],
      finished: dto.finished ?? false,
      finishedAt: dto.finishedAt ? new Date(dto.finishedAt) : null,
      createdAt: new Date(dto.createdAt),
      updatedAt: new Date(dto.updatedAt),
    };
  }
}
